import React, { useEffect, useState } from "react";
import { 
  Bell, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  Star,
  Upload,
  X,
  Save,
  Clock
} from "react-feather";
import { 
  collection, 
  getDocs, 
  doc, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "shared/utils/firebase";
import PageHeader from "shared/components/PageHeader/PageHeader";
import styles from "./AnnouncementManagement.module.css";

function AnnouncementManagement() {
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);

  const [announcementForm, setAnnouncementForm] = useState({
    title: "",
    content: "",
    category: "academic",
    priority: "medium",
    author: "",
    actionLink: "",
    posterUrl: "",
    expiresAt: "",
    targetAudience: []
  });

  const [uploadingPoster, setUploadingPoster] = useState(false);

  const handlePosterFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingPoster(true);
      const posterRef = ref(storage, `Posters/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(posterRef, file);

      uploadTask.on(
        'state_changed',
        null,
        (error) => {
          console.error("Storage upload failed, falling back to local reader:", error);
          const reader = new FileReader();
          reader.onloadend = () => {
            setAnnouncementForm(prev => ({ ...prev, posterUrl: reader.result }));
            setUploadingPoster(false);
          };
          reader.readAsDataURL(file);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setAnnouncementForm(prev => ({ ...prev, posterUrl: downloadURL }));
          setUploadingPoster(false);
        }
      );
    } catch (err) {
      console.error("Error uploading poster:", err);
      setUploadingPoster(false);
    }
  };

  const categories = ["academic", "career", "technical"];
  const priorities = ["high", "medium", "low"];

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    filterAnnouncements();
  }, [announcements, searchTerm, filterCategory, filterPriority]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const announcementsSnapshot = await getDocs(
        query(collection(db, "announcements"), orderBy("createdAt", "desc"))
      );
      const announcementsData = announcementsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        expiresAt: doc.data().expiresAt?.toDate()
      }));
      setAnnouncements(announcementsData);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterAnnouncements = () => {
    let filtered = announcements;

    if (searchTerm) {
      filtered = filtered.filter(announcement => 
        announcement.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        announcement.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        announcement.author?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory !== "all") {
      filtered = filtered.filter(announcement => announcement.category === filterCategory);
    }

    if (filterPriority !== "all") {
      filtered = filtered.filter(announcement => announcement.priority === filterPriority);
    }

    setFilteredAnnouncements(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAnnouncementForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const announcementData = {
        ...announcementForm,
        author: editingAnnouncement ? (editingAnnouncement.author || "Admin") : "Admin",
        expiresAt: announcementForm.expiresAt ? new Date(announcementForm.expiresAt) : null,
        createdAt: editingAnnouncement ? editingAnnouncement.createdAt : new Date(),
        updatedAt: new Date(),
        views: editingAnnouncement ? editingAnnouncement.views || 0 : 0,
        readBy: editingAnnouncement ? editingAnnouncement.readBy || [] : []
      };

      if (editingAnnouncement) {
        await updateDoc(doc(db, "announcements", editingAnnouncement.id), announcementData);
        setAnnouncements(announcements.map(announcement => 
          announcement.id === editingAnnouncement.id ? { ...announcement, ...announcementData } : announcement
        ));
      } else {
        const docRef = await addDoc(collection(db, "announcements"), announcementData);
        setAnnouncements([{ id: docRef.id, ...announcementData }, ...announcements]);
      }

      setShowAnnouncementModal(false);
      setEditingAnnouncement(null);
      resetForm();
    } catch (error) {
      console.error("Error saving announcement:", error);
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);

    setAnnouncementForm({
      title: announcement.title || "",
      content: announcement.content || "",
      category: announcement.category || "academic",
      priority: announcement.priority || "medium",
      author: announcement.author || "",
      actionLink: announcement.actionLink || "",
      posterUrl: announcement.posterUrl || "",
      expiresAt: announcement.expiresAt ? announcement.expiresAt.toISOString().split('T')[0] : "",
      targetAudience: announcement.targetAudience || []
    });
    setShowAnnouncementModal(true);
  };

  const handleDelete = async () => {
    if (!announcementToDelete) return;

    try {
      await deleteDoc(doc(db, "announcements", announcementToDelete.id));
      setAnnouncements(announcements.filter(announcement => announcement.id !== announcementToDelete.id));
      setShowDeleteModal(false);
      setAnnouncementToDelete(null);
    } catch (error) {
      console.error("Error deleting announcement:", error);
    }
  };

  const resetForm = () => {
    setAnnouncementForm({
      title: "",
      content: "",
      category: "academic",
      priority: "medium",
      author: "",
      actionLink: "",
      posterUrl: "",
      expiresAt: "",
      targetAudience: []
    });
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-danger';
      case 'medium': return 'bg-warning';
      case 'low': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading announcements...</p>
      </div>
    );
  }

  return (
    <div className={styles.announcementManagement}>
      <PageHeader
        icon={Bell}
        title="Announcement Management"
        subtitle="Create, schedule, and broadcast official platform notifications and announcements"
        badge={`${filteredAnnouncements.length} Announcements`}
        actions={
          <button 
            className={styles.addBtn}
            onClick={() => {
              setEditingAnnouncement(null);
              resetForm();
              setShowAnnouncementModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Add Announcement
          </button>
        }
      />

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search announcements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterGroup}>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Priorities</option>
            {priorities.map(priority => (
              <option key={priority} value={priority}>
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{announcements.length}</div>
          <div className={styles.statLabel}>Total Announcements</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {announcements.filter(a => a.category === 'academic').length}
          </div>
          <div className={styles.statLabel}>Academic</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {announcements.filter(a => a.priority === 'high').length}
          </div>
          <div className={styles.statLabel}>High Priority</div>
        </div>
      </div>

      <div className={styles.announcementList}>
        {filteredAnnouncements.map((announcement) => (
          <div key={announcement.id} className={styles.announcementCard}>
            <div className={styles.announcementHeader}>
              <div className={styles.announcementInfo}>
                <h5 className={styles.announcementTitle}>{announcement.title}</h5>
                <div className={styles.announcementMeta}>
                  <span className={`badge ${getPriorityColor(announcement.priority)}`}>
                    {announcement.priority}
                  </span>
                  <span className="badge bg-secondary">
                    {announcement.category}
                  </span>
                  {announcement.expiresAt && (
                    <span className="badge bg-danger">
                      <Clock size={12} className="me-1" />
                      Expires: {announcement.expiresAt instanceof Date ? announcement.expiresAt.toLocaleDateString() : new Date(announcement.expiresAt).toLocaleDateString()}
                    </span>
                  )}
                  {announcement.targetAudience && (Array.isArray(announcement.targetAudience) ? announcement.targetAudience.length > 0 : announcement.targetAudience) && (
                    <span className="badge bg-info">
                      Audience: {Array.isArray(announcement.targetAudience) ? announcement.targetAudience.join(', ') : announcement.targetAudience}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.announcementActions}>
                <button
                  className={styles.actionBtn}
                  onClick={() => handleEdit(announcement)}
                  title="Edit Announcement"
                >
                  <Edit size={16} />
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.deleteBtn}`}
                  onClick={() => {
                    setAnnouncementToDelete(announcement);
                    setShowDeleteModal(true);
                  }}
                  title="Delete Announcement"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className={styles.announcementContent}>
              <p>{announcement.content}</p>
            </div>

            <div className={styles.announcementFooter}>
              <div className={styles.announcementDetails}>
                <span>By {announcement.author || 'Admin'}</span>
                <span className="ms-3">
                  <Clock size={14} className="me-1" />
                  {announcement.createdAt ? (announcement.createdAt instanceof Date ? announcement.createdAt.toLocaleDateString() : new Date(announcement.createdAt).toLocaleDateString()) : 'N/A'}
                </span>
              </div>
              {announcement.actionLink && (
                <div className="mt-2 text-end">
                  <a
                    href={announcement.actionLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-primary"
                  >
                    Proceed Link &rarr;
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredAnnouncements.length === 0 && (
        <div className={styles.emptyState}>
          <Bell size={48} className="text-muted mb-3" />
          <h5>No announcements found</h5>
          <p className="text-muted">
            {searchTerm || filterCategory !== "all" || filterPriority !== "all" 
              ? "Try adjusting your search or filters"
              : "No announcements have been created yet"
            }
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingAnnouncement(null);
              resetForm();
              setShowAnnouncementModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Create First Announcement
          </button>
        </div>
      )}

      {/* Announcement Form Modal */}
      {showAnnouncementModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAnnouncementModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>{editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowAnnouncementModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={announcementForm.title}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Category *</label>
                  <select
                    name="category"
                    value={announcementForm.category}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Priority *</label>
                  <select
                    name="priority"
                    value={announcementForm.priority}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label>Expires At</label>
                  <input
                    type="date"
                    name="expiresAt"
                    value={announcementForm.expiresAt}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Content *</label>
                <textarea
                  name="content"
                  value={announcementForm.content}
                  onChange={handleInputChange}
                  required
                  rows="6"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Action Link (Optional URL for action button)</label>
                <input
                  type="text"
                  name="actionLink"
                  value={announcementForm.actionLink}
                  onChange={handleInputChange}
                  placeholder="e.g., https://... or /courses"
                  className={styles.formInput}
                />
              </div>

              {/* Announcement Poster / Info Document */}
              <div className={styles.formGroup}>
                <label>Announcement Poster / Info Document (JPG / Image)</label>
                <input
                  type="text"
                  name="posterUrl"
                  value={announcementForm.posterUrl}
                  onChange={handleInputChange}
                  placeholder="Paste image URL (e.g. https://.../poster.jpg)"
                  className={styles.formInput}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Or upload image file:</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,image/webp"
                    onChange={handlePosterFileChange}
                    style={{ fontSize: '0.85rem' }}
                  />
                  {uploadingPoster && (
                    <span className="small text-primary spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Uploading...</span>
                    </span>
                  )}
                </div>
                {announcementForm.posterUrl && (
                  <div style={{ marginTop: '8px', position: 'relative', display: 'inline-block' }}>
                    <img src={announcementForm.posterUrl} alt="Announcement Poster Preview" style={{ maxHeight: '120px', borderRadius: '6px', border: '1px solid #e2e8f0' }} />
                    <button
                      type="button"
                      style={{ position: 'absolute', top: '-6px', right: '-6px', borderRadius: '50%', padding: '2px 6px', background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer' }}
                      onClick={() => setAnnouncementForm(prev => ({ ...prev, posterUrl: "" }))}
                      title="Remove Poster"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAnnouncementModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  <Save size={16} className="me-2" />
                  {editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && announcementToDelete && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Confirm Deletion</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                Are you sure you want to delete the announcement <strong>"{announcementToDelete.title}"</strong>?
              </p>
              <p className="text-danger">
                This action cannot be undone.
              </p>
              <div className={styles.modalActions}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete Announcement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementManagement;

